import { Button, Chip, Dialog, DialogContent, DialogTitle } from '@mui/material';
import {
  Children,
  Dispatch,
  SetStateAction,
  isValidElement,
  useCallback,
  forwardRef,
  useMemo,
  useState,
  useEffect,
  useRef,
  type CSSProperties,
  type HTMLAttributes,
} from 'react';
import { fontSize, palette } from '../..';
import {
  addParameter,
  previewParameter,
  resetParameterSession,
  setParameterData,
  updateParameter,
} from '../../redux/Parameters/ParametersAction';
import {
  Box,
  FormControl,
  MenuItem,
  Select,
  Typography,
  FormControlLabel,
  Checkbox,
  Radio,
  Slider,
} from '@mui/material';
import { DatePicker, DateRangePicker } from 'rsuite';
import {
  formatNumberWithAbbrev,
  formatNumberWithComma,
} from '../ChartOptions/Format/NumberFormatter';
import 'rsuite/DateRangePicker/styles/index.css';
import { KeyboardArrowDown } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { FormatSettings, FormData } from '../../redux/Parameters/ParameterInterface';
import { RootState } from '../../redux';
import TrueFalse from '../Common/TrueFalse';
import { callQueryAPI } from '../../redux/ChartPoperties/ChartControlsActions';
import { updateAxesQueryParam } from '../../redux/ChartPoperties/ChartPropertiesActions';
import {
  normalizeUserSelection,
  resolveParameterDefaultValue,
  sanitizeParameterSelections,
} from './utils/parameterSelectionUtils';

const MENU_ITEM_HEIGHT = 30;
const MENU_MAX_HEIGHT = 270;
const MENU_VISIBLE_ITEMS = Math.max(1, Math.floor(MENU_MAX_HEIGHT / MENU_ITEM_HEIGHT));
const MENU_OVERSCAN_ITEMS = 8;
const MAX_VIRTUAL_SCROLL_HEIGHT = 8_000_000;

const VirtualizedMenuList = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  function VirtualizedMenuList(props, ref) {
    const { children, ...other } = props;
    const itemData = useMemo(() => Children.toArray(children), [children]);
    const itemCount = itemData.length;
    const viewportHeight = Math.min(itemCount, MENU_VISIBLE_ITEMS) * MENU_ITEM_HEIGHT;
    const totalContentHeight = itemCount * MENU_ITEM_HEIGHT;
    const scaledScrollHeight = Math.min(totalContentHeight, MAX_VIRTUAL_SCROLL_HEIGHT);
    const maxTopIndex = Math.max(0, itemCount - MENU_VISIBLE_ITEMS);
    const [scrollTop, setScrollTop] = useState(0);
    const handleScroll = useCallback((event: React.UIEvent<HTMLDivElement>) => {
      setScrollTop(event.currentTarget.scrollTop);
    }, []);

    const startIndex = useMemo(() => {
      if (itemCount === 0) return 0;

      const rawIndex =
        totalContentHeight <= MAX_VIRTUAL_SCROLL_HEIGHT
          ? Math.floor(scrollTop / MENU_ITEM_HEIGHT)
          : Math.floor(
              (scrollTop / Math.max(1, scaledScrollHeight - viewportHeight)) * maxTopIndex,
            );

      return Math.max(0, rawIndex - MENU_OVERSCAN_ITEMS);
    }, [itemCount, maxTopIndex, scaledScrollHeight, scrollTop, totalContentHeight, viewportHeight]);

    const endIndex = Math.min(itemCount, startIndex + MENU_VISIBLE_ITEMS + MENU_OVERSCAN_ITEMS * 2);

    const visibleItems = itemData.slice(startIndex, endIndex);
    const listStyle = (other.style as CSSProperties) || {};

    if (itemCount === 0) {
      return <div ref={ref} {...other} />;
    }

    return (
      <div
        ref={ref}
        {...other}
        onScroll={handleScroll}
        style={{
          ...listStyle,
          maxHeight: viewportHeight,
          overflowY: 'auto',
          overflowX: 'hidden',
          position: 'relative',
        }}
      >
        <div style={{ height: scaledScrollHeight, position: 'relative' }}>
          <div style={{ position: 'sticky', top: 0, left: 0, right: 0 }}>
            {visibleItems.map((item, index) => (
              <div
                key={
                  isValidElement(item) && item.key != null
                    ? String(item.key)
                    : `${startIndex + index}`
                }
                style={{ height: MENU_ITEM_HEIGHT }}
              >
                {item}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  },
);

interface ParameterPreviewPopUpProps {
  propKey: string;
  showPopup: boolean;
  setShowPopup: Dispatch<SetStateAction<boolean>>;
  formData: FormData;
  value: (string | number)[];
  setValue: Dispatch<SetStateAction<(string | number)[]>>;
  columns: any;
  setPreview: Dispatch<SetStateAction<boolean>>;
  preview: boolean;
  loadFromColumn: boolean;
  loadDefaultValue: boolean;
  formatOptions: FormatSettings;
  isRange: string;
}

const ParameterPreviewPopUp = ({
  propKey,
  showPopup,
  setShowPopup,
  formData,
  value,
  setValue,
  columns,
  setPreview,
  preview,
  loadFromColumn,
  loadDefaultValue,
  formatOptions,
  isRange,
}: ParameterPreviewPopUpProps) => {
  const { parameters, chartProperties } = useSelector((state: RootState) => ({
    tabTileProps: state.tabTileProps,
    parameters: state.parameters,
    chartProperties: state.chartProperties,
  }));
  const dispatch = useDispatch();
  const [inputValue, setInputValue] = useState(formData.defaultValue);
  const [checkboxValues, setCheckboxValues] = useState(() => {
    if (
      formData.defaultValue !== undefined &&
      formData.defaultValue !== null &&
      formData.defaultValue !== ''
    ) {
      if (!Array.isArray(value) || value.length === 0) {
        if (formData.dataType === 'number' || formData.dataType === 'decimal') {
          const min = Number(formData.min);
          const max = Number(formData.max);
          const step = Number(formData.step) || (formData.dataType === 'decimal' ? 0.1 : 1);
          const generatedValues: number[] = [];
          for (let v = min; v <= max; v = Number((v + step).toFixed(10))) {
            generatedValues.push(v);
          }
          if (generatedValues.includes(Number(formData.defaultValue))) {
            return [formData.defaultValue];
          }
        } else if (formData.dataType === 'date') {
          const startDate = new Date(formData.min);
          const endDate = new Date(formData.max);
          const step = Number(formData.step) || 1;
          const dateList: string[] = [];
          let current = new Date(startDate);
          while (current <= endDate) {
            dateList.push(current.toISOString().split('T')[0]);
            current.setDate(current.getDate() + step);
          }
          if (dateList.includes(formData.defaultValue as string)) {
            return [formData.defaultValue];
          }
        }
      } else {
        // Original logic when value array has items
        if (formData.dataType === 'number' || formData.dataType === 'decimal') {
          if (formData.defaultValue && value.map(Number).includes(Number(formData.defaultValue))) {
            return [formData.defaultValue];
          }
        } else {
          if (formData.defaultValue && value.includes(formData.defaultValue as any)) {
            return [formData.defaultValue];
          }
        }
      }
    }
    return [];
  });

  const [sliderValue, setSliderValue] = useState(() => {
    const min = Number(formData.min);
    const max = Number(formData.max);
    const defaultVal = Number(formData.defaultValue);

    if (defaultVal >= min && defaultVal <= max) {
      return defaultVal;
    }
    return min;
  });

  const [rangeValue, setRangeValue] = useState(() => {
    const min = Number(formData.min);
    const max = Number(formData.max);

    let rangeMin = min;
    let rangeMax = max;

    if (
      formData.defaultValueMin !== undefined &&
      formData.defaultValueMin !== '' &&
      Number(formData.defaultValueMin) >= min &&
      Number(formData.defaultValueMin) <= max
    ) {
      rangeMin = Number(formData.defaultValueMin);
    }

    if (
      formData.defaultValueMax !== undefined &&
      formData.defaultValueMax !== '' &&
      Number(formData.defaultValueMax) >= min &&
      Number(formData.defaultValueMax) <= max
    ) {
      rangeMax = Number(formData.defaultValueMax);
    }

    return [rangeMin, Number(formData.defaultValue), rangeMax];
  });

  const [calendarValue, setCalendarValue] = useState<Date[] | null>(() => {
    return [
      new Date(
        formData.defaultValueMin && new Date(formData.defaultValueMin) >= new Date(formData.min)
          ? formData.defaultValueMin
          : formData.min,
      ),
      new Date(
        formData.defaultValueMax && new Date(formData.defaultValueMax) <= new Date(formData.max)
          ? formData.defaultValueMax
          : formData.max,
      ),
    ];
  });

  const formatValue = (value: any) => {
    if (formatOptions.enableRounding) {
      value = Number(value).toFixed(formatOptions.roundingDigits);
    }

    if (formatOptions.numberSeparator === 'Abbrev' && formatOptions.formatValue !== 'Percent') {
      value = formatNumberWithAbbrev(value, formatOptions.roundingDigits);
    }
    if (formatOptions.formatValue === 'Percent') {
      value = value / 100;
      value = `${value}%`;
    }

    if (formatOptions.currencySymbol.length > 0) {
      value = `${formatOptions.currencySymbol}${value}`;
    }

    if (formatOptions.numberSeparator === 'Comma') {
      value = formatNumberWithComma(value);
    }
    return value;
  };

  const minFormattedValue = formatValue(formData.min);
  const maxFormattedValue = formatValue(formData.max);

  const defaultValue = formData.defaultValue as boolean;
  const [active, setActive] = useState<boolean>(defaultValue || false);

  const options = useMemo(() => {
    if (isRange === 'range') {
      if (formData.dataType === 'date') {
        const startDate = new Date(formData.min);
        const endDate = new Date(formData.max);
        const step = Number(formData.step) || 1;
        const dateList: string[] = [];
        let current = new Date(startDate);
        while (current <= endDate) {
          dateList.push(current.toISOString().split('T')[0]);
          current.setDate(current.getDate() + step);
        }
        return dateList;
      }
      if (formData.dataType === 'number' || formData.dataType === 'decimal') {
        const min = Number(formData.min);
        const max = Number(formData.max);
        const step = Number(formData.step) || 1;
        const numbers: number[] = [];
        for (let v = min; v <= max; v = Number((v + step).toFixed(10))) {
          numbers.push(v);
        }
        return numbers;
      }
    } else {
      let combined: (number | string)[] = [...value];

      const specialOptions = ['(Blank)', '(Empty)'];
      let filtered;

      if (formData.dataType === 'number' || formData.dataType === 'decimal') {
        const numbers = combined
          .filter((v) => !specialOptions.includes(v as string))
          .map((v) => Number(v));
        filtered = [
          ...Array.from(new Set(numbers)),
          ...specialOptions.filter((opt) => combined.includes(opt)),
        ];
      } else if (formData.dataType === 'date') {
        const normal = combined.filter((v) => !specialOptions.includes(v as string));
        filtered = [
          ...Array.from(new Set(normal)),
          ...specialOptions.filter((opt) => combined.includes(opt)),
        ];
      } else {
        const normal = combined.filter((v) => !specialOptions.includes(v as string));
        filtered = [
          ...Array.from(new Set(normal)),
          ...specialOptions.filter((opt) => combined.includes(opt)),
        ];
      }
      return filtered;
    }
    return [];
  }, [value, formData.min, formData.max, formData.step, formData.dataType, isRange]);

  // Add defaultValue to checkboxValues after options are computed (moved out of useMemo)
  useEffect(() => {
    if (!options || options.length === 0) return;

    if (isRange === 'range') {
      if (formData.dataType === 'date') {
        if (formData.defaultValue && options.includes(formData.defaultValue as string)) {
          setCheckboxValues((prev) =>
            prev.includes(formData.defaultValue as string)
              ? prev
              : [...prev, formData.defaultValue as string],
          );
        }
      } else if (formData.dataType === 'number' || formData.dataType === 'decimal') {
        if (
          formData.defaultValue !== undefined &&
          options.map(Number).includes(Number(formData.defaultValue))
        ) {
          setCheckboxValues((prev) =>
            prev.includes(formData.defaultValue as any)
              ? prev
              : [...prev, formData.defaultValue as any],
          );
        }
      }
    } else {
      if (
        formData.defaultValue !== undefined &&
        formData.defaultValue !== null &&
        options.includes(formData.defaultValue as any)
      ) {
        setCheckboxValues((prev) =>
          prev.includes(formData.defaultValue as any)
            ? prev
            : [...prev, formData.defaultValue as any],
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options]);

  // Chunked rendering state and logic for long lists
  const [menuOpen, setMenuOpen] = useState(false);
  const [renderCount, setRenderCount] = useState(() => Math.min(200, options?.length ?? 0));
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    setRenderCount(Math.min(200, options?.length ?? 0));
  }, [options]);

  const startChunkedRender = () => {
    if (rafRef.current) return;
    const chunkSize = 200;
    const step = () => {
      setRenderCount((prev) => {
        const next = Math.min(options.length, prev + chunkSize);
        if (next < options.length) {
          rafRef.current = requestAnimationFrame(step);
        } else {
          if (rafRef.current) {
            cancelAnimationFrame(rafRef.current);
            rafRef.current = null;
          }
        }
        return next;
      });
    };
    rafRef.current = requestAnimationFrame(step);
  };

  const stopChunkedRender = () => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    setRenderCount(Math.min(200, options?.length ?? 0));
  };

  const [radioValue, setRadioValue] = useState(() => {
    if (options && formData.defaultValue !== undefined && formData.defaultValue !== null) {
      if (formData.dataType === 'number' || formData.dataType === 'decimal') {
        return options.map(Number).includes(Number(formData.defaultValue))
          ? formData.defaultValue
          : '';
      }
      return options.includes(formData.defaultValue as any) ? formData.defaultValue : '';
    }
    return '';
  });

  const handleSwitchChange = () => {
    setActive((prev: boolean) => !prev);
  };

  const handleInputChange = (e: any) => {
    setInputValue(e.target.value);
  };

  const handleCheckboxChange = (event: any) => {
    const value = event.target.value;
    setCheckboxValues(typeof value === 'string' ? value.split(',') : value);
  };

  const handleRadioChange = (event: any) => {
    setRadioValue(event.target.value);
  };

  const handleCalendarRangeChange = (date: Date[]) => {
    setCalendarValue(date);
  };

  const handleSliderChange = (_: Event, newValue: number | number[]) => {
    const step = Number(formData.step) || (formData.dataType === 'decimal' ? 0.1 : 1);
    const decimalPlaces = step.toString().split('.')[1]?.length || 0;
    const adjustedValue = Number(
      (Math.round((newValue as number) / step) * step).toFixed(decimalPlaces),
    );
    const clampedValue = Math.min(
      Math.max(adjustedValue, Number(formData.min)),
      Number(formData.max),
    );
    setSliderValue(clampedValue);
  };

  const handleRangeChange = (event: Event, newValue: number | number[]) => {
    const [start, end] = newValue as number[];
    const step = Number(formData.step) || 1;

    const adjustedStart = Math.round(start / step) * step;
    const adjustedEnd = Math.round(end / step) * step;

    setRangeValue([adjustedStart, adjustedStart, adjustedEnd]);
  };

  const handlePreview = () => {
    setShowPopup(false);
    setPreview(true);

    // Validate defaultValue based on isRange type
    let validatedDefaultValue = formData.defaultValue;

    if (isRange === 'range') {
      // For range, defaultValue should be between min and max
      const numDefault = Number(validatedDefaultValue);
      const numMin = Number(formData.min);
      const numMax = Number(formData.max);

      if (numDefault <= numMin || numDefault >= numMax) {
        validatedDefaultValue = '';
      }
    } else {
      // For list, defaultValue should exist in value array
      if (formData.dataType === 'number' || formData.dataType === 'decimal') {
        if (!value.map(Number).includes(Number(validatedDefaultValue))) {
          validatedDefaultValue = '';
        }
      } else if (formData.dataType === 'date') {
        if (
          !value.some(
            (v: any) =>
              new Date(v).getTime() === new Date(validatedDefaultValue as string).getTime(),
          )
        ) {
          validatedDefaultValue = '';
        }
      } else {
        if (!value.includes(validatedDefaultValue as string)) {
          validatedDefaultValue = '';
        }
      }
    }

    const column = {
      dataType: formData.dataType,
      displayType: formData.displayType,
      defaultValue: validatedDefaultValue,
      fieldName: columns?.fieldName || '',
      tableId: columns?.tableId || '',
      calculatedFieldId: columns?.calculatedFieldId || '',
      has_calculation: columns?.has_calculation || false,
      aggregationType: columns?.aggregationType || 'none',
      defaultValueMin: isRange !== 'range' ? '' : formData.defaultValueMin,
      defaultValueMax: isRange !== 'range' ? '' : formData.defaultValueMax,
      originalDataType: columns?.originalDataType,
    };

    dispatch(
      previewParameter(
        propKey,
        true,
        formData.name,
        column,
        isRange,
        isRange === 'range' ? [] : value,
        isRange === 'range' ? formData.min : '',
        isRange === 'range' ? formData.max : '',
        isRange === 'range' ? formData.step : '',
        loadFromColumn,
        loadDefaultValue,
        formatOptions,
      ),
    );

    handleSaveParameter();
  };

  const updateUserFilterItem = (
    propKey: string,
    bIndex: number,
    itemIndex: number,
    field: any,
    axesName: string,
  ) => {
    dispatch(updateAxesQueryParam(propKey, bIndex, itemIndex, field, axesName));
  };

  // handle save parameter to add or update parameter in redux store
  const handleSaveParameter = () => {
    const parameterId = parameters.properties[propKey]?.parameterSession?.parameterId;
    if (!parameterId) return;
    if (parameters.properties[propKey]?.parameterSession?.mode === 'edit') {
      dispatch(updateParameter(propKey, parameterId));
      const chartFilers = parameters.properties[propKey]?.parameterSession?.chartFilter;
      if (chartFilers) {
        chartFilers.forEach((cf: any) => {
          const targetPropKey = cf?.propKey || propKey;
          const targetFields =
            chartProperties.properties[targetPropKey]?.chartAxes?.[0]?.fields || [];

          targetFields.some((field: any) => {
            if (field.parameterId === parameterId) {
              const normalizedSelection = normalizeUserSelection(
                formData.displayType || '',
                parameters.properties[propKey]?.parameterSession?.userSelection,
              );

              const currentSelectionValues =
                normalizedSelection.kind === 'array' || normalizedSelection.kind === 'range'
                  ? normalizedSelection.values
                  : normalizedSelection.kind === 'normal'
                    ? [normalizedSelection.value]
                    : [];

              field.userSelection = currentSelectionValues.filter(
                (v: any) => v !== '(All)' && v !== '',
              );
              // Validate userSelection against parameter values and range
              const validationContext = {
                dataType: formData.dataType,
                values: value,
                minValue: formData.min,
                maxValue: formData.max,
              };

              if (Array.isArray(field.userSelection)) {
                field.userSelection = sanitizeParameterSelections(
                  field.userSelection,
                  validationContext,
                );
              }

              const defaultValue = resolveParameterDefaultValue(
                formData.defaultValue,
                validationContext,
              );

              field.userSelection =
                Array.isArray(field.userSelection) && field.userSelection.length > 0
                  ? field.userSelection
                  : defaultValue
                    ? [defaultValue]
                    : [];
              dispatch(setParameterData(parameterId, field.userSelection));
              if (cf?.itemIndex !== undefined && cf?.currentChartAxesName) {
                updateUserFilterItem(
                  targetPropKey,
                  0,
                  cf.itemIndex,
                  field,
                  cf.currentChartAxesName,
                );
              }
              dispatch(callQueryAPI(targetPropKey, true));
              return true;
            }
            return false;
          });
        });
      }
    } else {
      dispatch(addParameter(propKey));
    }
    dispatch(resetParameterSession(propKey));
  };

  // useEffect(() => {
  //   if (formData.dataType === 'number' || formData.dataType === 'decimal') {
  //     if (columns.fieldName === '') {
  //       const min = Number(formData.min);
  //       const max = Number(formData.max);
  //       const step = Number(formData.step) || (formData.dataType === 'decimal' ? 0.1 : 1);
  //       const values: string[] = [];
  //       for (let v = min; v <= max; v = Number((v + step).toFixed(10))) {
  //         values.push(v.toString());
  //       }
  //       // setValue(values);
  //     }
  //   }
  // }, []);

  return (
    <div>
      <Dialog
        open={showPopup}
        maxWidth="md"
        sx={{
          overflowY: 'scroll',
          overflowX: 'hidden',
          maxHeight: '70vh',
          top: '10%',
        }}
        onClose={() => {
          setShowPopup(false);
        }}
      >
        <DialogTitle
          style={{
            fontSize: '1rem',
            fontWeight: 'bold',
            color: '#222831',
            textAlign: 'center',
            marginBottom: '1rem',
          }}
        >
          Preview Parameter
        </DialogTitle>
        <DialogContent
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            flexDirection: 'column',
            position: 'relative',
            height: '100%',
            width: '300px',
            minHeight: '15rem',
            // border: '1px solid #e0e0e0',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              // alignItems: 'start',
              flex: 1,
              gap: 0.5,
            }}
          >
            <Typography
              sx={{
                fontWeight: 500,
                transform: 'capitalize',
                color: '#1c1c1c',
                textAlign: 'start',
              }}
            >
              {formData.name}
            </Typography>
            {formData.displayType === 'input (single selection)' && (
              <input
                value={inputValue as string}
                onChange={handleInputChange}
                placeholder={formData.dataType === 'text' ? 'Enter a item' : 'Enter a value'}
                type={formData.dataType === 'text' ? 'text' : 'number'}
                style={{
                  fontSize: fontSize.medium,
                  width: '100%',
                  padding: '5px 10px',
                  border: '1px solid',
                  borderColor: 'rgba(110, 110, 110, 0.5)',
                  borderRadius: '4px',
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
                onFocus={(e) => (e.target.style.borderColor = '#1c1c1c')}
                onBlur={(e) => (e.target.style.borderColor = 'rgba(110, 110, 110, 0.5)')}
              />
            )}
            {formData.displayType === 'checkbox (multiple selection)' && (
              <FormControl fullWidth variant="outlined" size="small">
                <Select
                  multiple
                  value={checkboxValues}
                  onChange={handleCheckboxChange}
                  displayEmpty
                  onOpen={() => {
                    setMenuOpen(true);
                    setRenderCount(Math.min(200, options?.length ?? 0));
                    startChunkedRender();
                  }}
                  onClose={() => {
                    setMenuOpen(false);
                    stopChunkedRender();
                  }}
                  style={{
                    color: ' #1c1c1c',
                    minHeight: '1rem',
                    fontSize: fontSize.medium,
                    textAlign: 'left',
                  }}
                  IconComponent={KeyboardArrowDown}
                  renderValue={(selected) => {
                    if ((selected as string[]).length === 0) {
                      return (
                        <Typography
                          sx={{
                            color: '#718096',
                            fontSize: fontSize.medium,
                          }}
                        >
                          Select options...
                        </Typography>
                      );
                    }
                    return (
                      <div
                        style={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: '5px',
                          maxHeight: '5.2rem',
                          overflowY: 'auto',
                        }}
                      >
                        {(selected as string[]).map((value) => (
                          <Chip
                            key={value}
                            label={
                              columns.dataType === 'date'
                                ? new Date(value).toLocaleDateString()
                                : value
                            }
                            size="small"
                            sx={{
                              backgroundColor: '#f0f4f8',
                              fontSize: fontSize.extraSmall,
                            }}
                          />
                        ))}
                      </div>
                    );
                  }}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 270,
                        maxWidth: 30,
                        overflowY: 'hidden',
                        boxShadow: '0 5px 10px rgba(0,0,0,0.3)',
                        marginTop: '5px',
                      },
                    },
                    MenuListProps: {
                      component: VirtualizedMenuList,
                      disablePadding: true,
                      autoFocusItem: false,
                    },
                  }}
                  sx={{
                    '& .MuiSelect-select': {
                      padding: '0.25rem 0.5rem',
                    },
                    borderColor: 'rgba(110, 110, 110, 0.5)',
                    // borderColor: ' #6e6e6e',
                    borderRadius: '4px',
                    transition: 'all 0.2s ease',
                    '& .MuiOutlinedInput-notchedOutline': {
                      border: '1px solid',
                      borderColor: 'rgba(110, 110, 110, 0.5)',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: ' #1c1c1c',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      border: '1px solid',
                      borderColor: ' #1c1c1c',
                    },
                  }}
                >
                  {(options || []).slice(0, renderCount).map((option: any) => (
                    <MenuItem
                      key={option}
                      value={option}
                      sx={{
                        padding: '0',
                        minHeight: `${MENU_ITEM_HEIGHT}px`,
                        marginY: 0,
                        '&.Mui-selected': {
                          backgroundColor: 'rgba(132, 132, 132, 0.05)',
                          '&:hover': {
                            backgroundColor: 'rgba(132, 132, 132, 0.05)',
                          },
                        },
                        transition: 'all 0.1s ease',
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={
                              checkboxValues.includes(option) ||
                              checkboxValues.some((v: any) => v == option)
                            }
                            sx={{
                              '&.Mui-checked': {
                                color: '#404040',
                              },
                              width: '100%',
                              overflow: 'hidden',
                            }}
                            size="small"
                          />
                        }
                        label={option}
                        sx={{
                          margin: 0,
                          '& .MuiFormControlLabel-label': {
                            fontSize: fontSize.extraSmall,
                            color: '#2d3748',
                            fontWeight: '500',
                            textOverflow: 'ellipsis',
                          },
                        }}
                      />
                    </MenuItem>
                  ))}
                  {options.length > renderCount && (
                    <MenuItem
                      key="show-more"
                      disabled={false}
                      onClick={() => {
                        stopChunkedRender();
                        setRenderCount(options.length);
                      }}
                      sx={{ justifyContent: 'center', fontSize: fontSize.extraSmall }}
                    >
                      Show {options.length - renderCount} more...
                    </MenuItem>
                  )}
                </Select>
              </FormControl>
            )}
            {formData.displayType === 'radio (single selection)' && (
              <FormControl sx={{ height: '1.8rem' }} fullWidth size="small">
                <Select
                  value={radioValue || ''}
                  onChange={handleRadioChange}
                  size="small"
                  displayEmpty
                  onOpen={() => {
                    setMenuOpen(true);
                    setRenderCount(Math.min(200, options?.length ?? 0));
                    startChunkedRender();
                  }}
                  onClose={() => {
                    setMenuOpen(false);
                    stopChunkedRender();
                  }}
                  renderValue={(selected: any) => {
                    if (!selected) {
                      return (
                        <Typography
                          sx={{
                            color: '#718096',
                            fontSize: fontSize.medium,
                          }}
                        >
                          Select option
                        </Typography>
                      );
                    }
                    return selected;
                  }}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 270,
                        maxWidth: 30,
                        overflowY: 'hidden',
                      },
                    },
                    MenuListProps: {
                      component: VirtualizedMenuList,
                      disablePadding: true,
                      autoFocusItem: false,
                    },
                  }}
                  sx={{
                    fontSize: fontSize.extraSmall,
                    height: '100%',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#757575',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#222831',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#222831',
                    },
                    '& .MuiRadio-root': {
                      display: 'none',
                    },
                    pl: '0.5rem',
                  }}
                >
                  {(options || []).slice(0, renderCount).map((option: any) => (
                    <MenuItem
                      key={option}
                      value={option}
                      selected={radioValue == option}
                      sx={{
                        minHeight: `${MENU_ITEM_HEIGHT}px`,
                        paddingY: '0px',
                        '&.Mui-selected': {
                          backgroundColor: '#f5f5f5',
                          '&:hover': {
                            backgroundColor: '#eeeeee',
                          },
                        },
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Radio
                            checked={radioValue == option}
                            sx={{
                              color: '#757575',
                              '&.Mui-checked': {
                                color: '#222831',
                              },
                            }}
                          />
                        }
                        label={option}
                      />
                    </MenuItem>
                  ))}
                  {options.length > renderCount && (
                    <MenuItem
                      key="show-more"
                      disabled={false}
                      onClick={() => {
                        stopChunkedRender();
                        setRenderCount(options.length);
                      }}
                      sx={{ justifyContent: 'center', fontSize: fontSize.extraSmall }}
                    >
                      Show {options.length - renderCount} more...
                    </MenuItem>
                  )}
                </Select>
              </FormControl>
            )}
            {formData.displayType === 'slider (single selection)' && (
              <Box sx={{ width: '95%', margin: '0 auto' }}>
                <Slider
                  value={Number(sliderValue)}
                  onChange={handleSliderChange}
                  min={Number(formData.min)}
                  max={Number(formData.max)}
                  step={Number(formData.step) || (formData.dataType === 'decimal' ? 0.1 : 1)}
                  valueLabelDisplay="auto"
                  marks={[
                    { value: Number(formData.min), label: minFormattedValue },
                    { value: Number(formData.max), label: maxFormattedValue },
                  ]}
                  sx={{
                    color: '#787878',
                    zIndex: 10,
                    width: '100%',
                    '& .MuiSlider-thumb': {
                      width: '0.8rem',
                      height: '0.8rem',
                      color: '#939393',
                      '&:hover': {
                        boxShadow: '0px 0px 0px 8px rgba(0, 0, 0, 0.16) !important',
                      },
                      '&:active': {
                        boxShadow: '0px 0px 0px 8px rgba(0, 0, 0, 0.16) !important',
                      },
                      margin: '0 auto',
                    },
                    '& .MuiSlider-markLabel': {
                      '&[data-index="0"]': {
                        transform: 'translateX(0%)',
                      },
                      '&[data-index="1"]': {
                        transform: 'translateX(-100%)',
                      },
                      color: '#787878',
                      marginTop: '-0.2rem',
                    },
                    '& .MuiSlider-track': {
                      color: '#939393',
                    },
                    '& .Mui-focusVisible': {
                      boxShadow: '0px 0px 0px 8px rgba(0, 0, 0, 0.16) !important',
                    },
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    color: '#404040',
                    fontWeight: 500,
                    fontSize: fontSize.small,
                    mt: -3.9,
                    display: 'block',
                    textAlign: 'center',
                  }}
                >
                  {formatValue(sliderValue)}
                </Typography>
              </Box>
            )}
            {formData.displayType === 'range (range selection)' && (
              <Box sx={{ width: '95%', margin: '0 auto' }}>
                <Slider
                  value={[rangeValue[0], rangeValue[2]]}
                  onChange={handleRangeChange}
                  min={Number(formData.min)}
                  max={Number(formData.max)}
                  step={Number(formData.step) || 1}
                  valueLabelDisplay="auto"
                  marks={[
                    { value: Number(formData.min), label: minFormattedValue },
                    { value: Number(formData.max), label: maxFormattedValue },
                  ]}
                  sx={{
                    color: '#787878',
                    width: '100%',
                    '& .MuiSlider-thumb': {
                      width: '0.8rem',
                      height: '0.8rem',
                      color: '#939393',
                      '&:hover': {
                        boxShadow: '0px 0px 0px 8px rgba(0, 0, 0, 0.16) !important',
                      },
                      '&:active': {
                        boxShadow: '0px 0px 0px 8px rgba(0, 0, 0, 0.16) !important',
                      },
                    },
                    '& .MuiSlider-markLabel': {
                      '&[data-index="0"]': {
                        transform: 'translateX(0%)',
                      },
                      '&[data-index="1"]': {
                        transform: 'translateX(-100%)',
                      },
                      color: '#787878',
                      marginTop: '-0.2rem',
                    },
                    '& .MuiSlider-track': {
                      color: ' #A9A9A9',
                    },
                    '& .Mui-focusVisible': {
                      boxShadow: '0px 0px 0px 8px rgba(0, 0, 0, 0.16) !important',
                    },
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    color: '#404040',
                    fontWeight: 500,
                    fontSize: fontSize.small,
                    mt: -3.9,
                    display: 'block',
                    textAlign: 'center',
                  }}
                >
                  {formatValue(rangeValue[0])} - {formatValue(rangeValue[2])}
                </Typography>
              </Box>
            )}
            {formData.displayType === 'calendar (single selection)' && (
              <DatePicker
                style={{ width: '100%' }}
                container={() => document.querySelector('.MuiDialog-root')!} // default it appera behind the dialog
                placement="bottomStart"
                menuStyle={{ zIndex: 9999 }}
                defaultValue={new Date(formData.defaultValue as Date)}
                size="sm"
                format="yyyy-MM-dd"
                cleanable={false}
              />
            )}
            {formData.displayType === 'switch' && (
              <Box
                sx={{
                  border: '1px solid grey',
                  borderRadius: '5px',
                  display: 'flex',
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                  height: '1.5rem',
                  width: '100%',
                  gap: '0.5rem',
                  paddingInline: '0.2rem',
                  '&.axisFilterField': {
                    width: 'inherit',
                  },
                }}
              >
                <TrueFalse active={active} handleChange={handleSwitchChange} />
              </Box>
            )}
            {formData.displayType === 'calendar (range selection)' && (
              <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
                <DateRangePicker
                  style={{ width: '100%' }}
                  placement="bottomStart"
                  menuStyle={{ zIndex: 9999 }}
                  value={[calendarValue![0], calendarValue![1]]}
                  size="sm"
                  cleanable={false}
                  onChange={(date) => handleCalendarRangeChange(date!)}
                  format="yyyy-MM-dd"
                  // defaultValue={[new Date(formData.min), new Date(formData.max)]}
                />
              </Box>
            )}
          </Box>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              marginLeft: 'auto',
              gap: '10px',
              marginTop: '10px',
            }}
          >
            <Button
              style={{
                marginLeft: 'auto',
                color: 'white',
                border: 'none',
                padding: '3px 5px',
                fontSize: fontSize.medium,
                textTransform: 'none',
                backgroundColor: '#a7a7a7',
              }}
              variant="text"
              onClick={() => {
                setShowPopup(false);
              }}
            >
              Back
            </Button>
            <Button
              onClick={handlePreview}
              style={{
                marginLeft: 'auto',
                background: palette.primary.main,
                color: 'white',
                border: 'none',
                padding: '3px 5px',
                fontSize: fontSize.medium,
                textTransform: 'none',
              }}
            >
              Save
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ParameterPreviewPopUp;
